import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  Users, Copy, Share2, ArrowLeft, Loader2, Trophy,
  DollarSign, Gift, TrendingUp, Check, Clock,
} from 'lucide-react';

export default function ReferralDashboardPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      // Ensure we have a referral code
      await api.get('/referral/my-code');
      const res = await api.get('/referral/dashboard');
      if (res.data.success) setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchDashboard();
  }, [isAuthenticated, navigate, fetchDashboard]);

  const referralLink = data?.referral_code
    ? `${window.location.origin}/?ref=${data.referral_code}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Triple A Travel Solutions',
          text: 'Plan your next trip with AI! Use my referral link to get started.',
          url: referralLink,
        });
      } catch { /* cancelled */ }
    } else {
      handleCopy();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        </div>
        <Header />
        <div className="flex items-center justify-center pt-40">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      </div>
    );
  }

  const stats = data?.commission_stats || {};

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="referral-dashboard-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-10 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>
      <Header />

      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>

          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-7 h-7 text-gold" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">Referral Program</h1>
            <p className="text-white/80">Share your link, earn points & commissions</p>
          </div>

          {/* Referral Link Card */}
          <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl mb-6">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-ocean/60 font-medium mb-1">Your Referral Link</p>
                  <div className="bg-ocean/5 rounded-xl px-4 py-3 text-ocean font-mono text-sm truncate border border-ocean/10" data-testid="referral-link">
                    {referralLink}
                  </div>
                  <p className="text-xs text-ocean/50 mt-2">
                    Code: <span className="font-bold text-ocean" data-testid="referral-code">{data?.referral_code}</span>
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button onClick={handleCopy} variant="outline" className="border-ocean/30 text-ocean hover:bg-ocean/10 rounded-full px-5" data-testid="copy-link-btn">
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button onClick={handleShare} className="bg-gold hover:bg-gold-light text-ocean rounded-full px-5 font-semibold" data-testid="share-link-btn">
                    <Share2 className="w-4 h-4 mr-1" /> Share
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Referrals', value: data?.total_referrals || 0, icon: <Users className="w-5 h-5" />, color: 'from-teal-500 to-teal-600' },
              { label: 'Points Earned', value: data?.referral_points_earned || 0, icon: <Trophy className="w-5 h-5" />, color: 'from-amber-500 to-amber-600' },
              { label: 'Commissions Earned', value: `A$${(stats.total_earned || 0).toFixed(2)}`, icon: <DollarSign className="w-5 h-5" />, color: 'from-green-500 to-green-600' },
              { label: 'Points Balance', value: data?.points_balance || 0, icon: <Gift className="w-5 h-5" />, color: 'from-indigo-500 to-indigo-600' },
            ].map((stat) => (
              <Card key={stat.label} className="bg-white/95 backdrop-blur-xl border-white/30">
                <CardContent className="p-4 text-center">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mx-auto mb-2 text-white`}>
                    {stat.icon}
                  </div>
                  <p className="text-2xl font-bold text-ocean" data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}>{stat.value}</p>
                  <p className="text-xs text-ocean/60 mt-1">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Commission Breakdown */}
          {(stats.total_pending > 0 || stats.total_paid > 0 || stats.total_credited > 0) && (
            <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl mb-6">
              <CardHeader>
                <CardTitle className="font-serif text-lg text-ocean flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-gold" /> Commission Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-amber-50 rounded-xl">
                  <Clock className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-amber-700">A${(stats.total_pending || 0).toFixed(2)}</p>
                  <p className="text-xs text-amber-600">Pending</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-xl">
                  <DollarSign className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-green-700">A${(stats.total_paid || 0).toFixed(2)}</p>
                  <p className="text-xs text-green-600">Paid Out</p>
                </div>
                <div className="text-center p-3 bg-indigo-50 rounded-xl">
                  <Gift className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-indigo-700">A${(stats.total_credited || 0).toFixed(2)}</p>
                  <p className="text-xs text-indigo-600">Account Credit</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* How It Works */}
          <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl mb-6">
            <CardHeader>
              <CardTitle className="font-serif text-lg text-ocean">How It Works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  { step: '1', title: 'Share Your Link', desc: 'Send your unique referral link to friends, family, or clients.' },
                  { step: '2', title: 'They Sign Up', desc: 'When they register using your link, you earn 200 Triple A Miles instantly.' },
                  { step: '3', title: 'Earn Commissions', desc: 'When they book flights or hotels, you earn 15% commission on each booking.' },
                ].map((item) => (
                  <div key={item.step} className="text-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ocean to-sky text-white font-bold flex items-center justify-center mx-auto mb-3">
                      {item.step}
                    </div>
                    <h4 className="font-semibold text-ocean text-sm mb-1">{item.title}</h4>
                    <p className="text-xs text-ocean/60">{item.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Referrals */}
          {data?.referrals?.length > 0 && (
            <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl">
              <CardHeader>
                <CardTitle className="font-serif text-lg text-ocean">Recent Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.referrals.map((ref) => (
                    <div key={ref.id} className="flex items-center justify-between py-2 border-b border-ocean/10 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-ocean">{ref.referred_user_email}</p>
                        <p className="text-xs text-ocean/50">
                          {new Date(ref.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${ref.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {ref.status === 'completed' ? '+200 pts' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
